/**
 * Static legal + policy content. Kept in one file for easy legal review.
 * Each entry becomes its own SEO-crawlable URL (/policies/:slug).
 *
 * Copy is India-tuned for DPDPA + Razorpay + payment-gateway requirements.
 * Replace `SUPPORT_EMAIL`, `COMPANY_NAME`, and the Grievance Officer details
 * before going live.
 */

export const COMPANY = {
    legal_name: "Naukridaddy HR Solutions Private Limited",
    trading_name: "NaukriDaddy",
    website: "https://naukridaddy.com",
    support_email: "info@naukridaddy.com",
    privacy_email: "privacy@naukridaddy.com",
    grievance_email: "grievance@naukridaddy.com",
    grievance_officer_name: "Grievance Officer",
    founded: "November 2022",
    address_lines: [
        "Naukridaddy HR Solutions Private Limited",
        "Delhi, India",
    ],
    last_updated: "05 July 2026",
};

export const POLICIES = {
    "refund": {
        title: "Refund & Cancellation Policy",
        intro:
            "All purchases on NaukriDaddy are digital in nature — pricing reflects instant delivery " +
            "of AI compute, database write access, and cross-workspace visibility that cannot be " +
            "reclaimed once consumed. As a rule, we do not offer refunds.",
        sections: [
            {
                h: "1. General principle — no refund",
                p: "All fees paid for Pro Pass, Application Boost, CV Rewrite, Interview Prep credits, " +
                    "Verified Badge, Featured Candidate, All-in-One Bundle, or SaaS subscriptions are " +
                    "non-refundable once the purchase is complete. This applies whether or not you " +
                    "actively use the feature during its validity window.",
            },
            {
                h: "2. Narrow exceptions",
                p: "We WILL refund in these specific cases: (a) duplicate charge due to a payment-gateway " +
                    "error, verified against our transaction log; (b) service outage of more than 48 hours " +
                    "during a paid Pro Pass or Featured window, prorated to the outage duration; (c) fraudulent " +
                    "or unauthorised transactions confirmed by your bank. Requests must be raised within " +
                    "7 days of the charge.",
            },
            {
                h: "3. Verified Badge — pre-KYC refund",
                p: "If you paid ₹299 for a Verified Badge but did NOT upload any KYC document within " +
                    "24 hours, you may request a full refund at support@naukridaddy.com. Once KYC is " +
                    "uploaded, the ₹299 becomes non-refundable because compute + moderator time has been consumed.",
            },
            {
                h: "4. Interview Prep credits — unused",
                p: "Interview Prep credits do not expire but are non-refundable. If you leave the platform, " +
                    "your unused credits are forfeited on account closure.",
            },
            {
                h: "5. SaaS subscription cancellation",
                p: "Agency SaaS plans (Free / Pro / Enterprise) can be cancelled at any time from the Billing page. " +
                    "Cancellation stops future billing; the current billing period is used to completion and is " +
                    "not refunded. Downgrades from Enterprise to Pro or Free take effect at the start of the " +
                    "next billing cycle.",
            },
            {
                h: "6. How to request a refund",
                p: "Email {{grievance_email}} with (a) your registered email, (b) the payment ID from Razorpay, " +
                    "(c) which of the exceptions above you're invoking, and (d) any supporting evidence. Our team " +
                    "acknowledges within 3 business days and resolves within 10 business days.",
            },
        ],
    },
    "grievances": {
        title: "Grievance Redressal Policy",
        intro:
            "In compliance with the Digital Personal Data Protection Act, 2023 (India) and the Consumer " +
            "Protection (E-Commerce) Rules, 2020, we operate a formal grievance process with a named officer.",
        sections: [
            {
                h: "1. Grievance Officer",
                p: "{{grievance_officer_name}} · {{grievance_email}} · " +
                    "Response acknowledgment within 48 hours · Resolution within 30 days as prescribed by " +
                    "the Consumer Protection Rules.",
            },
            {
                h: "2. What we accept as a grievance",
                p: "Any complaint about (a) failure of a paid feature, (b) mishandling of your personal data " +
                    "or DPDPA rights, (c) refusal to honour a legitimate refund, (d) misuse of your profile " +
                    "in the Talent Marketplace, (e) discriminatory or harassing behaviour by a recruiter using " +
                    "the platform.",
            },
            {
                h: "3. How to file",
                p: "Email {{grievance_email}} with a subject line starting with 'GRIEVANCE:'. Include your " +
                    "registered email, a factual account of the issue, dates, and any screenshots or transaction IDs.",
            },
            {
                h: "4. Escalation",
                p: "If unresolved, you may escalate to the Data Protection Board of India (once notified) or " +
                    "file a complaint on the National Consumer Helpline (https://consumerhelpline.gov.in) or " +
                    "the local consumer forum with jurisdiction over your address.",
            },
        ],
    },
    "fraud": {
        title: "Fraud Prevention & Anti-Fraud Policy",
        intro:
            "We work with Razorpay's risk engine and our own behavioural signals to detect and reverse " +
            "fraudulent activity. Fraud attempts against our platform, our recruiters, or our candidates " +
            "are treated as criminal offences under Sections 66C and 66D of the Information Technology Act.",
        sections: [
            {
                h: "1. What counts as fraud",
                p: "Chargebacks after successful service consumption; use of stolen cards; account takeover; " +
                    "impersonation of another candidate or recruiter; uploading a fake resume or fabricated " +
                    "credentials for the Verified Badge; artificial inflation of referral leaderboard counts; " +
                    "circumventing the daily marketplace view quota with automation.",
            },
            {
                h: "2. Detection signals",
                p: "Velocity checks on view/reveal endpoints; device-fingerprint mismatch on impersonation " +
                    "cookies; card BIN + IP-country mismatches; multiple accounts with the same phone number; " +
                    "flagged patterns from Razorpay's risk API.",
            },
            {
                h: "3. Consequences of confirmed fraud",
                p: "Immediate account suspension without refund of any accrued balances; forfeiture of " +
                    "referral coins and Interview Prep credits; permanent IP ban; escalation to the customer's " +
                    "issuing bank; report filed with the local Cyber Crime cell where warranted.",
            },
            {
                h: "4. Chargeback policy",
                p: "If a payment you made is charged back through your bank after you have consumed the service, " +
                    "the corresponding feature (Verified Badge, Pro Pass, etc.) is immediately revoked, and the " +
                    "account is placed on hold pending resolution. We will provide Razorpay + our own logs as " +
                    "evidence in the bank's dispute process.",
            },
            {
                h: "5. Reporting fraud you noticed",
                p: "See something suspicious — a fake recruiter, a phishing message, a candidate impersonation? " +
                    "Email {{support_email}} with 'FRAUD' in the subject. We investigate within 24 hours.",
            },
        ],
    },
    "payment": {
        title: "Payment Policy",
        intro:
            "NaukriDaddy uses Razorpay Software Private Limited as its Payment Aggregator. All " +
            "transactions are processed on Razorpay's PCI-DSS-compliant infrastructure. NaukriDaddy " +
            "never stores your card number, CVV, or bank credentials.",
        sections: [
            {
                h: "1. Accepted payment methods",
                p: "UPI (all VPAs), credit and debit cards on Visa/Mastercard/RuPay/Amex networks, " +
                    "net-banking with 50+ Indian banks, wallets (Paytm, PhonePe, Amazon Pay), and " +
                    "EMI where the customer's bank supports it.",
            },
            {
                h: "2. Pricing",
                p: "All prices are shown in Indian Rupees (INR) inclusive of GST unless explicitly marked " +
                    "otherwise. Applicable GST is 18% on all digital services under the current CGST/SGST " +
                    "regime. GST invoices are available in the My Purchases page.",
            },
            {
                h: "3. Currency",
                p: "We accept payments in INR only. International cards are supported if your issuing " +
                    "bank permits INR transactions.",
            },
            {
                h: "4. Payment confirmation",
                p: "A payment is deemed successful only when Razorpay returns a verified webhook AND our " +
                    "signature-check passes on the returned razorpay_signature. If your card is charged " +
                    "but the feature is not unlocked, contact {{support_email}} with your order_id and " +
                    "payment_id — we reconcile every 24 hours.",
            },
            {
                h: "5. Subscription auto-renewal",
                p: "For features on auto-renewal (SaaS plans, Featured Candidate auto-renew), you authorise " +
                    "Razorpay to charge your saved payment instrument at the start of each billing cycle. " +
                    "You may cancel auto-renewal at any time from Billing → Cancel auto-renew.",
            },
            {
                h: "6. Failed payments",
                p: "If your payment fails, no charge is captured. If you see a debit on your statement but " +
                    "no feature unlock, this is a pending hold — banks typically release it within 5-7 " +
                    "business days. If it doesn't reverse, email us with your bank statement excerpt.",
            },
        ],
    },
    "privacy": {
        title: "Privacy Policy",
        intro:
            "We take your privacy seriously and process personal data in accordance with the Digital " +
            "Personal Data Protection Act, 2023 (DPDPA). This policy describes what we collect, why we " +
            "collect it, and how you can exercise your rights.",
        sections: [
            {
                h: "1. Data we collect",
                p: "Account: name, email, phone, password hash. " +
                    "Profile: resume text, skills, experience, location, current title. " +
                    "Financial: payment IDs and receipts (never card details). " +
                    "Behavioural: page views, feature clicks, marketplace views/reveals, impersonation " +
                    "events (for admins). KYC (Verified Badge only): Aadhaar/PAN document scans — " +
                    "encrypted at rest, redacted after admin review.",
            },
            {
                h: "2. Why we collect it",
                p: "To operate the ATS + Marketplace product; to compute AI CV rewrites and interview " +
                    "prep questions personalised to you; to invoice you; to prevent fraud; to satisfy " +
                    "legal obligations (GST, DPDPA audit trail).",
            },
            {
                h: "3. Third-party processors",
                p: "Razorpay (payments), Resend (transactional email), Google Cloud (infrastructure), " +
                    "Anthropic via Emergent LLM Key (AI matching / interview prep / CV rewrite), Gupshup " +
                    "(WhatsApp notifications, when enabled). All processors are bound by data-protection " +
                    "agreements.",
            },
            {
                h: "4. Your rights under DPDPA",
                p: "Right to access, correct, and erase your data. Right to withdraw consent (via " +
                    "/candidate/privacy → Revoke consent). Right to grievance redressal (see the " +
                    "Grievance Redressal Policy). Right to nominate someone to exercise your rights if " +
                    "you become incapacitated.",
            },
            {
                h: "5. Data retention",
                p: "Active-account data: for as long as you use NaukriDaddy. Deleted-account data: purged " +
                    "within 30 days of your erasure request, except records legally required to retain " +
                    "(tax invoices for 8 years under GST rules, audit-trail rows for the mandatory " +
                    "DPDPA period).",
            },
            {
                h: "6. Contact",
                p: "Data-protection questions: {{privacy_email}}. Erasure requests: {{privacy_email}} " +
                    "with subject 'DPDPA ERASURE'.",
            },
        ],
    },
    "terms": {
        title: "Terms of Service",
        intro:
            "These Terms govern your use of NaukriDaddy. By creating an account, uploading a resume, " +
            "or paying for any feature, you accept these Terms and our Privacy Policy.",
        sections: [
            {
                h: "1. Eligibility",
                p: "You must be at least 18 years old. If you're representing an organisation (agency, " +
                    "employer), you warrant that you have authority to bind that organisation.",
            },
            {
                h: "2. Your content",
                p: "You keep ownership of your resume, cover letter, and profile data. You grant NaukriDaddy " +
                    "a limited licence to process it as described in the Privacy Policy — including AI " +
                    "processing, visibility to recruiters where you've consented, and inclusion in " +
                    "aggregated analytics.",
            },
            {
                h: "3. Acceptable use",
                p: "No spamming candidates, no scraping, no automation that circumvents quotas, no fake " +
                    "resumes, no discrimination on protected grounds, no attempting to gain unauthorised " +
                    "access to another workspace.",
            },
            {
                h: "4. Feature limits and quotas",
                p: "Free plan gets 5 Marketplace views per recruiter per day; Pro 50; Enterprise unlimited. " +
                    "Verified Badge requires KYC. Interview Prep spends 1 credit per session. Featured " +
                    "Candidate is 30 days per ₹99. All limits are subject to change with 15 days' notice.",
            },
            {
                h: "5. Termination",
                p: "You may close your account at any time. We may suspend accounts for fraud, abuse, or " +
                    "violations of these Terms. On termination, paid features cease immediately with no " +
                    "refund of unused validity.",
            },
            {
                h: "6. Limitation of liability",
                p: "NaukriDaddy is a platform — we do not guarantee employment outcomes. Our liability " +
                    "for any claim is capped at the total amount you paid us in the 12 months preceding " +
                    "the claim.",
            },
            {
                h: "7. Governing law",
                p: "These Terms are governed by the laws of India. Disputes will be resolved by the courts " +
                    "of Delhi.",
            },
        ],
    },
    "shipping": {
        title: "Delivery & Service Fulfilment Policy",
        intro:
            "NaukriDaddy is a digital service — there is no physical shipping. This page describes when " +
            "and how paid features are delivered to your account, in line with Razorpay's compliance " +
            "requirements for online-merchants.",
        sections: [
            {
                h: "1. Instant delivery",
                p: "Pro Pass, Application Boost, Interview Prep credits, Featured Candidate, and the " +
                    "All-in-One Bundle are unlocked on your account within 30 seconds of successful payment " +
                    "confirmation from Razorpay. If your account doesn't reflect the unlock, force-refresh " +
                    "the page or log out and back in.",
            },
            {
                h: "2. Human-review delivery",
                p: "Verified Badge (₹299) is unlocked in two stages: (a) instant payment confirmation, " +
                    "(b) KYC review by our compliance team within 48 hours during business days. You'll " +
                    "receive an email when the badge is approved or if any KYC document needs re-upload.",
            },
            {
                h: "3. Failure handling",
                p: "If Razorpay confirms payment but the feature isn't delivered, our reconciliation " +
                    "job runs every 24 hours and auto-unlocks. If a manual fix is needed, email " +
                    "{{support_email}} with your order_id.",
            },
        ],
    },
};


// Interpolate placeholders like {{grievance_email}} in section bodies.
export function renderPolicySection(text) {
    return text.replace(/\{\{(\w+)\}\}/g, (_, key) => COMPANY[key] || "");
}
